import type { HTMLComponent } from "@commercekit/react";
import type { MouseEvent } from "react";
import { useEffect, useMemo, useRef } from "react";
import type { Product } from "@commercekit/api";
import classNames from "classnames";
import WvCloseIcon from "../atoms/WvCloseIcon";
import WvProductSwiper from "../organisms/WvProductSwiper";
import WvProductSlideThumbnail from "./WvProductSlideThumbnail";
import { useCarousel } from "~/hooks/useCarousel";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { buildProductSlides, productSlideKey } from "~/lib/productSlides";
import type { ProductGalleryVideo } from "~/lib/youtubeUtils";

const WvProductPreview: HTMLComponent<{
  images: Product["images"];
  videos?: ProductGalleryVideo[];
  name: string;
  activeIndex: number;
  onClose(): void;
}> = ({ images, videos, name, activeIndex, onClose }) => {
  const wheelGestures = WheelGesturesPlugin();
  const mainSwiper = useCarousel(
    {
      containScroll: "trimSnaps",
      startIndex: activeIndex,
      draggable: true,
      loop: true,
    },
    [wheelGestures]
  );

  const slides = useMemo(
    () => buildProductSlides(images, videos),
    [images, videos]
  );

  const activeThumbRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    activeThumbRef.current?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
  }, [mainSwiper.selectedIndex]);

  // Anything outside the white card is backdrop and closes the preview.
  const closeOnBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest(".product-preview__card")) return;
    onClose();
  };

  return (
    <>
      <div
        className="lg-backdrop product-preview__backdrop in"
        style={{
          transitionDuration: "150ms",
        }}
      ></div>

      <div
        tabIndex={-1}
        aria-modal="true"
        aria-label={name}
        role="dialog"
        onClick={closeOnBackdropClick}
        className="product-preview lg-outer lg-visible preview"
      >
        <div className="product-preview__card">
          <div className="product-preview__header">
            <span className="product-preview__title">{name}</span>
            <button
              type="button"
              aria-label="Close gallery"
              className="product-preview__close"
              onClick={onClose}
            >
              <WvCloseIcon size={15} />
            </button>
          </div>
          <WvProductSwiper
            isPreview
            images={images}
            videos={videos}
            embla={mainSwiper}
            onClose={onClose}
          />
          {slides.length > 1 && (
            <div className="product-preview__thumbs">
              {slides.map((slide, index) => {
                const active = mainSwiper.selectedIndex === index;
                return (
                  <button
                    key={productSlideKey(slide, index)}
                    ref={active ? activeThumbRef : undefined}
                    type="button"
                    aria-label={`${index + 1} / ${slides.length}`}
                    aria-current={active || undefined}
                    className={classNames("product-preview__thumbs-item", {
                      "product-preview__thumbs-item--active": active,
                    })}
                    onClick={() => mainSwiper.scrollTo(index)}
                  >
                    <WvProductSlideThumbnail slide={slide} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WvProductPreview;
